import { expandBySelector, ensureActiveBySelector } from '../../primitives/GuidedTour';
import { SPREAD_TYPES, SPREAD_TYPE_ORDER } from '../../../utils/spreadMappings';

/**
 * Spread Simulator guided-tour step catalog.
 *
 * All spread-specific tour content and prepare helpers live here — the
 * GuidedTour engine and useTourState hook (components/primitives/) stay
 * module-agnostic, exactly like the screener's catalog in
 * Screener/screenerTourSteps.js.
 *
 * The tour is deliberately a lesson, not just a feature walkthrough: the
 * Spread Simulator exists to TEACH vertical spreads, so the copy leads with
 * the concepts (debit vs credit, anchor + width, capped payoff, return on
 * risk) and uses the UI as the worked example. Copy mirrors the tooltips in
 * utils/spreadMappings.js and the pricing semantics in
 * stock_analyzer/services/spreads/pricing.py.
 *
 * The form phase auto-populates a DEMO selection as its steps appear (via the
 * form's `tourPopulate(part, value)` ref method — only-if-empty, so a user's
 * own selections are never overwritten). The spread-type step renders choice
 * buttons so the learner picks which of the four spreads the demo runs with;
 * that choice uses `tourSetSpreadType`, which always applies. The final form
 * step's primary button becomes "Simulate", clicking the real Run button so
 * the results phase resumes on live data.
 *
 * Anchors (data-tour attributes) referenced below:
 *   Form:    spread-tickers, spread-type, spread-expiration, spread-anchor,
 *            spread-width, spread-speed, spread-actions
 *   Results: spread-view-toggle, spread-view-heatmap, spread-view-detail,
 *            spread-heatmap-settings, spread-heatmap-controls,
 *            spread-heatmap-table (+ per-column data-tour-col and per-scenario
 *            data-tour-scenario), spread-row-actions, spread-column-tabs
 *            (+ per-tab data-tour-group), spread-sim-settings
 */

// --- Demo selection populated by the tour (only into empty sections) --------
// One expiration window keeps the demo to a single chain fetch; two anchors x
// two widths give the heatmap enough contrast to teach from (4 spreads).
const DEMO = {
  tickers: ['AAPL'],
  type: 'call_debit',
  expiration: ['medium'],
  anchor: ['atm', 'otm-5'],
  width: ['w-5', 'w-10'],
};

// --- Spread-specific prepare helpers ---------------------------------------

// AccordionFormSection: root .accordion-section, header .section-header,
// expanded content .section-content.expanded. One-way expand (never collapses).
const expandAccordion = (sel) =>
  expandBySelector(sel, '.section-header', '.section-content.expanded');

// View switching: SpreadsTab's toggle buttons carry .active when selected.
export const ensureDetailView = () =>
  ensureActiveBySelector('[data-tour="spread-view-detail"]', 'active');
export const ensureHeatmapView = () =>
  ensureActiveBySelector('[data-tour="spread-view-heatmap"]', 'active');

// Both shared settings panels render a .simulation-settings root whose first
// child is the clickable header; expanded content contains a range slider.
const expandSettingsPanel = (wrapperSel) =>
  expandBySelector(wrapperSel, '.simulation-settings > div:first-child', 'input[type="range"]');

// Small settle delay so a just-clicked view toggle can mount its tree before
// the engine polls for the step's anchor.
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

// Short teaching descriptions for the four spread-type choice buttons.
const CHOICE_DESCRIPTIONS = {
  call_debit: 'Bullish, pay up front. The beginner-friendly one — risk is simply what you paid.',
  put_credit: 'Bullish, collect up front. Income style: keep the credit if the stock holds up.',
  put_debit: 'Bearish, pay up front. Profit as the stock falls, risk capped at the debit.',
  call_credit: 'Bearish, collect up front. Keep the credit if the stock stays below your short strike.',
};

/**
 * @param {Object} formHandle - ref-like handle to SpreadForm exposing
 *   getFormState() / tourPopulate(part, value) / tourSetSpreadType(key).
 *   Optional — steps degrade gracefully when absent.
 */
export function getSpreadTourSteps(formHandle) {
  const formState = () => formHandle?.current?.getFormState?.() || {};

  // Expand the step's accordion section, then fill it with the demo value if
  // the user hasn't picked anything there yet.
  const expandAndPopulate = (sel, part) => () => {
    expandAccordion(sel);
    if (formHandle?.current?.tourPopulate && DEMO[part] != null) {
      formHandle.current.tourPopulate(part, DEMO[part]);
    }
  };

  const selectedConfig = () => SPREAD_TYPES[formState().spreadType] || SPREAD_TYPES.call_debit;

  const formSteps = [
    {
      id: 'welcome',
      target: null,
      placement: 'center',
      title: 'Welcome to the Spread Simulator',
      body: [
        'A vertical spread is two option contracts — "legs" — of the same type and expiration at different strikes: you buy one and sell the other. Selling the second leg pays for part of the first, and in exchange your profit is capped.',
        'That cap is the whole point: unlike a single option, a vertical spread has a known maximum profit AND a known maximum loss from the moment you open it.',
        'This tour teaches the four spread types while building a real demo simulation step by step. Skip anytime — relaunch it from the Guide button next to the page title.',
      ],
    },
    {
      id: 'tickers',
      target: '[data-tour="spread-tickers"]',
      prepare: expandAndPopulate('[data-tour="spread-tickers"]', 'tickers'),
      title: 'Pick your stocks',
      body: 'We\'ve added AAPL as a demo — search and add or remove tickers anytime. Every spread combination you configure is simulated against every ticker here.',
    },
    {
      id: 'spread-type',
      target: '[data-tour="spread-type"]',
      prepare: () => {
        expandAccordion('[data-tour="spread-type"]');
        // Seed a default so "Next = keep current" always has a valid selection.
        if (formHandle?.current?.tourPopulate) {
          formHandle.current.tourPopulate('type', DEMO.type);
        }
      },
      title: 'The four vertical spreads',
      body: [
        'Every vertical spread is two decisions. First, your outlook: the top row is bullish, the bottom row bearish. Second, how you pay for it — and this is the debit/credit distinction.',
        'DEBIT spreads (Call Debit, Put Debit): you BUY the leg nearest the money and sell a further one to cheapen it. You pay the difference up front, and that debit is the most you can lose. You profit when the stock moves your way and the spread widens toward its maximum value.',
        'CREDIT spreads (Put Credit, Call Credit): you SELL the leg nearest the money and buy a further one as insurance. You collect the difference up front and keep it if the stock simply stays on your side of the short strike — time decay works for you. Your max loss is the strike width minus that credit.',
        'Pick one to learn with — the demo will simulate your choice:',
      ],
      choices: SPREAD_TYPE_ORDER.map((key) => ({
        key,
        label: SPREAD_TYPES[key].label,
        description: CHOICE_DESCRIPTIONS[key],
        get selected() {
          return formState().spreadType === key;
        },
        onSelect: () => formHandle?.current?.tourSetSpreadType?.(key),
      })),
    },
    {
      id: 'expiration',
      target: '[data-tour="spread-expiration"]',
      prepare: expandAndPopulate('[data-tour="spread-expiration"]', 'expiration'),
      title: 'Expiration: both legs share it',
      body: [
        'A vertical uses ONE expiration for both legs — that\'s what makes the max profit and max loss exact numbers.',
        'Shorter expirations decay faster: that hurts debit spreads (you own the decaying value) and helps credit spreads (you sold it). Longer expirations give a debit thesis time to play out but make a credit spread wait longer to keep its premium.',
        'We\'ve selected Medium-Term for the demo — a balanced window.',
      ],
    },
    {
      id: 'anchor',
      target: '[data-tour="spread-anchor"]',
      prepare: expandAndPopulate('[data-tour="spread-anchor"]', 'anchor'),
      title: 'Anchor strike: where you take your stand',
      // Body is resolved at step activation so it can phrase buy/sell from the
      // learner's actual spread-type choice.
      body: () => {
        const config = selectedConfig();
        const verb = config.isCredit ? 'SELL' : 'BUY';
        const role = config.isCredit
          ? 'For your credit spread this is the leg you SELL — the premium you collect. The closer to the money you sell it, the more you collect, but the less the stock has to move against you before the spread hurts.'
          : 'For your debit spread this is the leg you BUY. ITM anchors cost more but win more often; OTM anchors are cheap but need the stock to come to them.';
        return [
          `The anchor is the leg nearest the money — the one you position, and the one you ${verb}.`,
          role,
          'We\'ve selected ATM and Slight OTM so the results show both characters side by side.',
        ];
      },
    },
    {
      id: 'width',
      target: '[data-tour="spread-width"]',
      prepare: expandAndPopulate('[data-tour="spread-width"]', 'width'),
      title: 'Width: the biggest lever',
      body: [
        'Width is how far the second leg sits from the anchor, as a % of the stock price. It defines the total pot: max profit plus max loss always equals the width × $100 per contract.',
        'Narrow spreads are cheap and hit their cap fast — good for modest expected moves. Wide spreads cost more (or collect more) and behave closer to the outright option, with a much higher ceiling.',
        'We\'ve selected Narrow (5%) and Balanced (10%) so you can compare the trade-off directly.',
      ],
    },
    {
      id: 'speed',
      target: '[data-tour="spread-speed"]',
      prepare: () => expandAccordion('[data-tour="spread-speed"]'),
      title: 'Exit timing',
      body: 'When you plan to close the position — most spread traders exit before expiration rather than ride the last days of decay. This seeds the simulation\'s holding period; after the run you can drag the Days-to-Exit slider to explore any exit date without re-running.',
    },
    {
      id: 'actions',
      target: '[data-tour="spread-actions"]',
      nextLabel: 'Simulate',
      onNext: () => {
        document.querySelector('[data-tour="spread-actions"] button')?.click();
      },
      title: 'Run it',
      body: [
        'The button shows how many spreads your selections produce — every expiration × anchor × width combination, per ticker, is priced with real market quotes: each leg gets its own Black-Scholes projection, then they\'re netted.',
        'Click Simulate below — we\'ll run this demo and the tour continues automatically in the results.',
      ],
    },
  ];

  // --- Concrete heatmap example (one spread, one scenario column) ----------
  // Chosen fresh on every activation of the example step; the target selector
  // is a function re-evaluated per engine poll tick, so it also handles the
  // table mounting a beat after the view switch.
  let examplePct = null;
  const EXAMPLE_ROW = '[data-tour="spread-heatmap-table"] tbody tr:first-child';

  const pickScenarioPct = () => {
    const ths = document.querySelectorAll('[data-tour="spread-heatmap-table"] thead [data-tour-scenario]');
    const vals = Array.from(ths)
      .map((th) => parseFloat(th.getAttribute('data-tour-scenario')))
      .filter((v) => !isNaN(v));
    if (vals.length === 0) return null;
    // Prefer the positive move closest to +5% (a default scenario column).
    const pool = vals.some((v) => v > 0) ? vals.filter((v) => v > 0) : vals;
    return pool.reduce((best, v) => (Math.abs(v - 5) < Math.abs(best - 5) ? v : best), pool[0]);
  };

  const readCellText = (sel) => {
    const el = document.querySelector(sel);
    if (!el) return null;
    const text = (el.textContent || '').trim();
    return text || null;
  };

  const HEADERS = '[data-tour="spread-heatmap-table"] thead';

  const resultsSteps = [
    {
      id: 'view-toggle',
      target: '[data-tour="spread-view-toggle"]',
      prepare: ensureHeatmapView,
      title: 'Two views of the same results',
      body: 'The Elasticity Heatmap (the default) shows each spread\'s projected return across stock-move scenarios. The Detail View is the full data table, including the two individual legs behind every spread. The tour covers the heatmap first.',
    },
    {
      id: 'heatmap-settings',
      target: '[data-tour="spread-heatmap-settings"]',
      prepare: async () => {
        ensureHeatmapView();
        await wait(150);
        expandSettingsPanel('[data-tour="spread-heatmap-settings"]');
      },
      title: 'Days to Exit and scenario columns',
      body: [
        'The slider sets how long you hold before closing — as fixed days or as a % of each spread\'s life (it starts at 50% of the shortest expiration). Every projection recalculates instantly for that exit date, right in your browser.',
        'The scenario chips control which stock-move columns appear. The defaults are tighter than the single-option screener\'s on purpose: past the short strike a vertical is already at max profit, so extreme columns add nothing.',
      ],
    },
    {
      id: 'capped-payoff',
      target: `${HEADERS} [data-tour-col="net-entry"]`,
      extraTargets: [
        `${HEADERS} [data-tour-col="max-profit"]`,
        `${HEADERS} [data-tour-col="max-loss"]`,
        `${HEADERS} [data-tour-col="breakeven"]`,
      ],
      title: 'The numbers that define a spread',
      body: [
        'Net Entry is what the spread costs (Dr = debit you pay) or pays you (Cr = credit you collect), per share.',
        'Max Profit (green) and Max Loss (red) are both hard caps — the defining property of a vertical. They always sum to the strike width × $100: a debit risks the debit and can make the rest of the width; a credit keeps the credit and risks the rest.',
        'Breakeven is the stock price where the position breaks even at expiration — it always sits the entry amount away from the anchor strike, in the direction that hurts you.',
      ],
    },
    {
      id: 'display-controls',
      target: '[data-tour="spread-heatmap-controls"]',
      title: 'Return on Risk: the honest yardstick',
      body: [
        'Because a spread\'s payoff is capped, raw percentage gains can mislead. Return on Risk % — projected P&L divided by your max loss — measures every spread against the capital it actually puts at risk, so debit and credit spreads become directly comparable.',
        'Spread Value Gain % is the alternative: the % change in the spread\'s own market value, signed from your position\'s side. On the right: Favorites and CSV export.',
      ],
    },
    {
      id: 'heatmap-example',
      prepare: () => {
        ensureHeatmapView();
        examplePct = null; // re-picked by the target function once the table is in the DOM
      },
      target: () => {
        if (examplePct == null) examplePct = pickScenarioPct();
        if (examplePct == null) return null; // no scenario columns → engine auto-skips
        return `${EXAMPLE_ROW} [data-tour-scenario="${examplePct}"]`;
      },
      extraTargets: () => [
        `${HEADERS} [data-tour-scenario="${examplePct}"]`,
        `${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`,
      ],
      title: 'Read one cell like a sentence',
      body: () => {
        const pct = examplePct;
        const ticker = readCellText(`${EXAMPLE_ROW} [data-tour-col="ticker"]`) || 'the first spread';
        const spreadName = readCellText(`${EXAMPLE_ROW} [data-tour-col="spread-type"]`) || 'spread';
        const entryText = readCellText(`${EXAMPLE_ROW} [data-tour-col="net-entry"]`) || '';
        const days = readCellText(`${EXAMPLE_ROW} [data-tour-col="days-to-exit"]`);
        const cellText = pct != null
          ? readCellText(`${EXAMPLE_ROW} [data-tour-scenario="${pct}"]`)
          : null;

        const isCredit = /Cr$/.test(entryText);
        const dir = pct > 0 ? 'rises' : pct < 0 ? 'drops' : 'stays flat';
        const movePhrase = pct === 0 ? 'stays flat' : `${dir} ${Math.abs(pct)}%`;
        const outcome = !cellText || cellText === '--'
          ? 'shows the projected result of exactly that move'
          : `is projected to return ${cellText} of the capital at risk`;
        const daysPhrase = days ? `${days} days out — the highlighted Days-to-Exit value` : 'your exit date';
        const mechanics = isCredit
          ? 'As a credit spread it profits when the spread it sold gets CHEAPER — the stock staying on your side and time passing both help.'
          : 'As a debit spread it profits when the spread it owns gets more valuable — it needs the stock to actually make the move.';

        return [
          'Each row is one spread; each colored column is a stock-move scenario.',
          `Take the first row — the ${ticker} ${spreadName} spread (${entryText}). The highlighted header is the scenario: ${ticker} ${movePhrase}. The cell is the payoff: if that move happens by your exit date (${daysPhrase}), this spread ${outcome}. ${mechanics}`,
          'Notice how cells stop improving past a point: that\'s the cap. Hover any cell for the projected spread value, underlying price, P&L in dollars, and the exit breakeven.',
        ];
      },
    },
    {
      id: 'row-actions',
      target: '[data-tour="spread-row-actions"]',
      title: 'Keep or dismiss',
      body: 'The star pins a spread as a favorite — it floats to the top of any sort and enables the Favorites filter. The minus hides a row you\'ve ruled out. Both carry over when you switch between the heatmap and the Detail View.',
    },
    {
      id: 'detail-legs',
      target: '[data-tour="spread-column-tabs"]',
      prepare: async () => {
        ensureDetailView();
        await wait(250);
        ensureActiveBySelector('[data-tour="spread-column-tabs"] [data-tour-group="Legs"]', 'tab-selected');
        await wait(150);
      },
      title: 'The two contracts behind every spread',
      body: [
        'The Legs tab shows what you\'d actually place with your broker: the long leg you buy and the short leg you sell, each with its own premium, implied volatility and delta. The simulator prices each leg separately — the two strikes sit at different points on the volatility smile.',
        'The net Greeks summarize the combined position: Net Delta is your directional exposure, and Net Theta is daily time decay — positive Net Theta means time is on your side, which is the credit-spread trade in a nutshell.',
      ],
    },
    {
      id: 'detail-sim',
      target: '[data-tour="spread-sim-settings"]',
      prepare: async () => {
        ensureDetailView();
        await wait(200);
        expandSettingsPanel('[data-tour="spread-sim-settings"]');
      },
      title: 'Stress-test any scenario',
      body: 'The Detail View\'s Simulation Settings add an Underlying Gain slider alongside Days to Target — drag either and every simulated column recomputes instantly in your browser. No new data is fetched, so experiment freely: try the move that would pin the stock right at your short strike.',
    },
    {
      id: 'done',
      target: null,
      placement: 'center',
      title: "You're set",
      body: [
        'That\'s the framework: pick an outlook (bullish or bearish), decide whether to pay a debit or collect a credit, place the anchor strike, choose a width — then judge every candidate by its Return on Risk, knowing both sides are capped.',
        'Try the same setup as a debit and as a credit and compare them in the heatmap — they\'re mirror images of the same structure. Relaunch this tour anytime from the Guide button.',
      ],
    },
  ];

  return { formSteps, resultsSteps };
}
